import { changedFields } from "@/lib/changed-fields";
import { useReportUnsavedEdits } from "@/hooks/use-report-unsaved-edits";

/** A person's editable profile fields as the form inputs hold them. Profile settings ›
 *  General and a member's Profile tab edit the same five. */
interface ProfileDraft {
  firstName: string;
  lastName: string;
  displayName: string;
  mobile: string;
  holidayRegion: string;
}

interface SavedProfileFields {
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  mobile_number?: string | null;
  country_region?: string | null;
}

/** What Save sends for a draft. Save and `hasUnsavedProfileEdits` both go through this,
 *  so "unsaved" always means "Save would change something" (NG-2823). */
export function profileSaveBody(draft: ProfileDraft) {
  return {
    first_name: draft.firstName.trim(),
    last_name: draft.lastName.trim(),
    display_name: draft.displayName.trim(),
    mobile_number: draft.mobile.trim() || null,
    country_region: draft.holidayRegion,
  };
}

/** Whether Save would change `saved`: typing a value back, or adding only surrounding
 *  spaces, is not an unsaved edit. `saved` is compared as the inputs load it (`null` as
 *  ""), so a freshly loaded form is never dirty. */
export function hasUnsavedProfileEdits(draft: ProfileDraft, saved: SavedProfileFields): boolean {
  const loaded: ProfileDraft = {
    firstName: saved.first_name ?? "",
    lastName: saved.last_name ?? "",
    displayName: saved.display_name ?? "",
    mobile: saved.mobile_number ?? "",
    holidayRegion: saved.country_region ?? "",
  };
  return changedFields(profileSaveBody(draft), profileSaveBody(loaded)).length > 0;
}

/** Reports whether `draft` holds edits Save has not written to `saved` (`null` while
 *  nothing is loaded: never dirty), through `useReportUnsavedEdits`. */
export function useReportUnsavedProfileEdits(
  draft: ProfileDraft,
  saved: SavedProfileFields | null,
  onDirtyChange: ((dirty: boolean) => void) | undefined,
): void {
  useReportUnsavedEdits(saved !== null && hasUnsavedProfileEdits(draft, saved), onDirtyChange);
}
